/**
 * V4.2 P0 — Damage / Accident Warning Badge.
 *
 * Rendered on the Listing Detail page when `card.damage_flags` is
 * populated. Damaged autos are hidden from the default DealFeed and
 * Top Deals by the backend, so users only reach a flagged listing via:
 *   • a direct URL (a saved link, a share, a friend's message);
 *   • admin views;
 *   • explicit `include_over_market=true` opt-in (future advanced filter).
 *
 * The badge translates the CRITICAL slug set into user-friendly
 * German. Multiple flags render as multiple pills so the user sees
 * the FULL picture ("Unfall + Motorschaden" ≠ just "Unfall").
 */
import React from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function DamageWarnBadge({ flags, className = "" }) {
  const { t } = useTranslation("listing");
  if (!Array.isArray(flags) || flags.length === 0) return null;
  return (
    <div
      data-testid="damage-warn-badge"
      className={`flex flex-wrap items-center gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 ${className}`}
    >
      <AlertTriangle
        size={16}
        strokeWidth={2}
        className="text-rose-400 shrink-0"
        aria-hidden="true"
      />
      <span className="text-[11px] font-semibold uppercase tracking-widest text-rose-300">
        {t("attention")}
      </span>
      <div className="flex flex-wrap gap-1">
        {flags.map((slug) => (
          <span
            key={slug}
            data-testid={`damage-flag-${slug}`}
            className="rounded-full border border-rose-500/50 bg-rose-500/15 px-2 py-0.5 text-xs font-medium text-rose-200"
          >
            {t(`damage.${slug}`, { defaultValue: slug })}
          </span>
        ))}
      </div>
    </div>
  );
}
